import { PlayerStats } from '../types/player';

/**
 * Safely access a property with a default value if undefined
 */
const safeValue = <T>(value: T | undefined, defaultValue: T): T => {
  return value !== undefined ? value : defaultValue;
};

/**
 * Calculates the Goal Involvement per 90 minutes
 * Formula: (Goals + Assists) / (Minutes Played / 90)
 * 
 * @param stats Player statistics including goals, assists, and minutes played
 * @returns The calculated Goal Involvement per 90 minutes, or 0 if no valid stats are provided
 */
export const calculateGoalInvolvementPer90 = (stats: PlayerStats | Partial<PlayerStats>): number => {
  if (!stats) {
    return 0;
  }
  
  const minutesPlayed = safeValue(stats.minutes_played, 0);
  const goals = safeValue(stats.goals, 0);
  const assists = safeValue(stats.assists, 0);
  
  if (minutesPlayed === 0) {
    return 0;
  }
  
  const minutesIn90s = minutesPlayed / 90;
  return (goals + assists) / minutesIn90s;
};

/**
 * Calculates the Discipline Risk Index
 * Formula: (Yellow Cards + Red Cards * 2) / Appearances
 * 
 * @param stats Player statistics including yellow cards, red cards, and appearances
 * @returns The calculated Discipline Risk Index, or 0 if no valid stats are provided
 */
export const calculateDisciplineRiskIndex = (stats: PlayerStats | Partial<PlayerStats>): number => {
  if (!stats) {
    return 0;
  }
  
  const appearances = safeValue(stats.appearances, 0);
  const yellowCards = safeValue(stats.yellow_cards, 0);
  const redCards = safeValue(stats.red_cards, 0);
  
  if (appearances === 0) {
    return 0;
  }
  
  return (yellowCards + redCards * 2) / appearances;
};

/**
 * Calculates the Minutes per Goal
 * Formula: Minutes Played / Goals
 * 
 * @param stats Player statistics including minutes played and goals
 * @returns The calculated Minutes per Goal, or Infinity if no goals scored
 */
export const calculateMinutesPerGoal = (stats: PlayerStats | Partial<PlayerStats>): number => {
  if (!stats) {
    return Infinity;
  }
  
  const minutesPlayed = safeValue(stats.minutes_played, 0);
  const goals = safeValue(stats.goals, 0);
  
  if (goals === 0) {
    return Infinity;
  }
  
  return minutesPlayed / goals;
};

/**
 * Calculates the Minutes per Assist
 * Formula: Minutes Played / Assists
 * 
 * @param stats Player statistics including minutes played and assists
 * @returns The calculated Minutes per Assist, or Infinity if no assists
 */
export const calculateMinutesPerAssist = (stats: PlayerStats | Partial<PlayerStats>): number => {
  if (!stats) {
    return Infinity;
  }
  
  const minutesPlayed = safeValue(stats.minutes_played, 0);
  const assists = safeValue(stats.assists, 0);
  
  if (assists === 0) {
    return Infinity;
  }
  
  return minutesPlayed / assists;
};

/**
 * Formats a statistic for display, handling special cases like Infinity
 * 
 * @param value The numeric value to format
 * @param precision The number of decimal places to show
 * @returns A formatted string representation of the value
 */
export const formatStat = (value: number, precision: number = 2): string => {
  if (!isFinite(value)) {
    return 'N/A';
  }
  
  return value.toFixed(precision);
};